import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from 'api/apiClient/apiClient'
import { isEqual } from 'helpers/global.helper/global.helper'
import i18n from 'i18next'
import { useTranslation } from 'react-i18next'
import alertStore from 'store/alert/alert'
import authStore from 'store/auth/auth'
import { useAuth } from 'ui/components/AuthContext/AuthContext'

export const SYSTEM_QUERY_KEY = `system`
export const SYSTEMS_QUERY_KEY = `systems`

export type SystemUpdateDataRequest = {
  primary_digital_twin_id: number | undefined
}

export function useSystems() {
  const { user, activeSystem, setActiveSystem } = useAuth()

  return useQuery({
    queryKey: [SYSTEMS_QUERY_KEY],

    queryFn: () =>
      apiClient<System[]>(`systems`).then((results) => {
        if (!results) {
          return []
        }

        if (results.length > 0) {
          if (!activeSystem) {
            // No active system set, set it to the user's default.
            const system = results.find((system) => system.id === user?.system) ?? results[0]
            setActiveSystem(system)
            authStore.activeSystem = system
          } else {
            // Update the active system to make sure localStorage has the most recent version.
            const system = results?.find((system) => system.id === activeSystem.id) ?? results[0]
            if (!isEqual(system, activeSystem)) {
              setActiveSystem(system)
              authStore.activeSystem = system
            }
          }
        }

        return results
      }),
  })
}

/**
 * Whether the first `/api/systems/` attempt has settled: answered, failed once, or paused offline.
 * Until then the active system is only what localStorage cached, if anything.
 */
export function useActiveSystemIsSettled(): boolean {
  const { isPending, failureCount, fetchStatus } = useSystems()
  return !isPending || failureCount > 0 || fetchStatus === `paused`
}

export function useSystem({ id }: { id?: number }) {
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: [SYSTEM_QUERY_KEY, id],
    queryFn: () => apiClient<System>(`systems/${id}`),

    initialData: () => {
      const state = queryClient.getQueryState<System[], Error>([SYSTEMS_QUERY_KEY])

      if (state?.data && id !== null && Date.now() - state.dataUpdatedAt <= 60 * 1000) {
        return state.data.find((d) => d.id === id)
      }
    },

    enabled: !!id,
  })
}

export function useUpdateSystem() {
  const { t } = useTranslation()
  const { setActiveSystem } = useAuth()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: SystemUpdateDataRequest }) =>
      apiClient<System>(`systems/${id}`, { method: 'PATCH', data }),

    onSuccess: (data) => {
      if (data) {
        setActiveSystem(data)
        authStore.activeSystem = data
        alertStore.success(t('Update successful'))
      }
    },

    onError: () => {
      alertStore.error(t(`Update failed. Try again or contact us if the problem persists.`))
    },
  })
}

export function useSystemAutomaticOptimizationToggleMutation() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id }: { id: number }) =>
      apiClient(`systems/${id}/toggle_automatic_optimization`, {
        method: `PATCH`,
      }),

    onMutate: async ({ id }: { id: number }) => {
      const key = [SYSTEM_QUERY_KEY, id]
      // Canceling any conflicting queries to not overwrite the optimistic update
      await queryClient.cancelQueries({
        queryKey: key,
      })

      // Saving previous system for rollback if error
      const previousSystem = queryClient.getQueryData<System | undefined>(key)

      // Optimistically change system
      queryClient.setQueryData<System | undefined>(key, (old) =>
        old ? { ...old, automatic_optimization: !old.automatic_optimization } : old
      )

      // Return rollback function
      return () => queryClient.setQueryData(key, previousSystem)
    },

    onError: (_error, _id, rollback) => {
      rollback?.()
      alertStore.error(t(`Automatic optimization failed to activate! Try again or contact us if the problem persists.`))
    },
  })
}

export function useUploadOpusFile() {
  const { activeSystem } = useAuth()

  return useMutation({
    mutationFn: ({ file }: { file: File }) => {
      if (!activeSystem) {
        return Promise.reject(new Error('activeSystem is undefined'))
      }
      const formData = new FormData()
      formData.append('file', file)
      return apiClient(`systems/${activeSystem.id}/upload_opus_file`, {
        method: 'POST',
        body: formData,
      })
    },

    onSuccess: () => {
      alertStore.success(i18n.t('Upload successful'))
    },

    onError: (e: { detail: string }) => {
      alertStore.error(`${i18n.t(`Upload failed`)}: ${e.detail}`)
    },
  })
}

export function useGetAllFilterTags(system_id?: number) {
  return useQuery({
    queryKey: [SYSTEM_QUERY_KEY, system_id, 'tags'],

    queryFn: async () => {
      const allTags = await apiClient<string[]>(`digital_twin/tags`, {
        method: 'GET',
        params: {
          system_id,
        },
      })
      return allTags
    },

    enabled: system_id !== null && system_id !== undefined,
  })
}
